import { useEffect, useRef } from "react";
import {
  DELIVERY_ROUTE_COLOR,
  DELIVERY_ROUTE_STRONG_COLOR,
  DEPOT_ROUTE_COLOR,
  LOADING_ROUTE_COLOR,
  LOADING_ROUTE_STRONG_COLOR,
} from "../utils/routeModelsData.js";

const LOADING_ROUTE_SELECTED_STROKE = LOADING_ROUTE_STRONG_COLOR;
const LOADING_ROUTE_SELECTED_FILL = LOADING_ROUTE_COLOR;
const DELIVERY_ROUTE_SELECTED_STROKE = DELIVERY_ROUTE_STRONG_COLOR;
const DELIVERY_ROUTE_SELECTED_FILL = DELIVERY_ROUTE_COLOR;
const ROUTE_SELECTION_FOCUS_ZOOM_LEVEL = 11;
const ROUTE_MAP_BADGE_Z_INDEX_OFFSET = 200;
const ROUTE_MAP_ACTIVE_BADGE_Z_INDEX_OFFSET = 1200;
const ROUTE_MAP_ACTIVE_BADGE_TRANSLATE = "0px -18px";
const ROUTE_MAP_ACTIVE_BADGE_FILTER = "drop-shadow(0 8px 18px rgba(15, 92, 87, 0.28))";

function hasMountedMarker(mapState, marker) {
  return Boolean(mapState?.map)
    && mapState.isDisposed !== true
    && Boolean(marker)
    && mapState.map.hasLayer?.(marker) === true;
}

function hasTrackedBadgeMarker(mapState, marker) {
  if (!mapState?.map || mapState.isDisposed || !marker) {
    return false;
  }

  return mapState.stopBadgeLayer?.hasLayer?.(marker) === true || mapState.map.hasLayer?.(marker) === true;
}

function resolveBadgeMarkerElement(marker) {
  return marker?.getElement?.() || marker?._icon || null;
}

function getRouteMapBadgeElements(mapState) {
  const mapContainer = typeof mapState?.map?.getContainer === "function"
    ? mapState.map.getContainer()
    : null;
  return mapContainer && typeof mapContainer.querySelectorAll === "function"
    ? Array.from(mapContainer.querySelectorAll(".route-map-stop-index"))
    : [];
}

function hasRouteMapBadgeAnchor(markerElement, routeNumber = null) {
  const normalizedRouteNumber = Number.parseInt(routeNumber, 10);
  return Number.isInteger(normalizedRouteNumber)
    && markerElement instanceof HTMLElement
    && markerElement.classList.contains(`route-map-stop-index--anchor-${normalizedRouteNumber}`);
}

function clearFocusedBadgeMarkers(mapState) {
  if (!mapState?.map || mapState.isDisposed) {
    return;
  }

  (Array.isArray(mapState.badgeMarkers) ? mapState.badgeMarkers : []).forEach((marker) => {
    if (!hasTrackedBadgeMarker(mapState, marker)) {
      return;
    }

    marker.closeTooltip?.();
    marker.setZIndexOffset?.(ROUTE_MAP_BADGE_Z_INDEX_OFFSET);

    const markerElement = resolveBadgeMarkerElement(marker);
    if (markerElement && typeof markerElement.style === "object") {
      markerElement.style.zIndex = "";
      markerElement.style.translate = "";
      markerElement.style.filter = "";
      markerElement.style.opacity = "";
      markerElement.style.visibility = "";
      markerElement.style.pointerEvents = "";
    }
  });

  getRouteMapBadgeElements(mapState).forEach((markerElement) => {
    if (markerElement && typeof markerElement.style === "object") {
      markerElement.style.zIndex = "";
      markerElement.style.translate = "";
      markerElement.style.filter = "";
      markerElement.style.opacity = "";
      markerElement.style.visibility = "";
      markerElement.style.pointerEvents = "";
    }
  });
}

function hideOverlappingBadgeMarkers(mapState, activeMarker) {
  if (!mapState?.map || mapState.isDisposed || !activeMarker || !Array.isArray(mapState.badgeMarkers)) {
    return;
  }

  const activeRouteNumber = Number.isInteger(activeMarker?.__routeNumber) ? activeMarker.__routeNumber : null;

  mapState.badgeMarkers.forEach((marker) => {
    if (!hasTrackedBadgeMarker(mapState, marker) || marker === activeMarker) {
      return;
    }

    marker.closeTooltip?.();
    marker.setZIndexOffset?.(ROUTE_MAP_BADGE_Z_INDEX_OFFSET);

    const markerElement = resolveBadgeMarkerElement(marker);
    if (markerElement && typeof markerElement.style === "object") {
      markerElement.style.opacity = "0";
      markerElement.style.visibility = "hidden";
      markerElement.style.pointerEvents = "none";
    }
  });

  getRouteMapBadgeElements(mapState).forEach((markerElement) => {
    if (!markerElement || typeof markerElement.style !== "object") {
      return;
    }

    if (hasRouteMapBadgeAnchor(markerElement, activeRouteNumber)) {
      return;
    }

    markerElement.style.opacity = "0";
    markerElement.style.visibility = "hidden";
    markerElement.style.pointerEvents = "none";
  });
}

function emphasizeBadgeMarker(mapState, marker) {
  if (!mapState?.map || mapState.isDisposed || !marker) {
    return;
  }

  clearFocusedBadgeMarkers(mapState);
  hideOverlappingBadgeMarkers(mapState, marker);
  marker.setZIndexOffset?.(ROUTE_MAP_ACTIVE_BADGE_Z_INDEX_OFFSET);

  const routeNumber = Number.isInteger(marker?.__routeNumber) ? marker.__routeNumber : null;
  const markerElement = resolveBadgeMarkerElement(marker);
  if (markerElement && typeof markerElement.style === "object") {
    markerElement.style.zIndex = String(ROUTE_MAP_ACTIVE_BADGE_Z_INDEX_OFFSET);
    markerElement.style.translate = ROUTE_MAP_ACTIVE_BADGE_TRANSLATE;
    markerElement.style.filter = ROUTE_MAP_ACTIVE_BADGE_FILTER;
  }

  if (routeNumber != null) {
    getRouteMapBadgeElements(mapState).forEach((candidateElement) => {
      if (!hasRouteMapBadgeAnchor(candidateElement, routeNumber)) {
        return;
      }

      if (candidateElement && typeof candidateElement.style === "object") {
        candidateElement.style.zIndex = String(ROUTE_MAP_ACTIVE_BADGE_Z_INDEX_OFFSET);
        candidateElement.style.translate = ROUTE_MAP_ACTIVE_BADGE_TRANSLATE;
        candidateElement.style.filter = ROUTE_MAP_ACTIVE_BADGE_FILTER;
      }
    });
  }
}

function resolveClientBadgeMarker(mapState, clientId = "", stopType = "") {
  if (!mapState?.map || mapState.isDisposed || !Array.isArray(mapState.badgeMarkers) || !clientId) {
    return null;
  }

  let fallbackMarker = null;
  for (const marker of mapState.badgeMarkers) {
    if (!hasTrackedBadgeMarker(mapState, marker) || marker.__clientId !== clientId) {
      continue;
    }

    if (!fallbackMarker) {
      fallbackMarker = marker;
    }

    if (!stopType || !marker.__stopType || marker.__stopType === stopType) {
      return marker;
    }
  }

  return fallbackMarker;
}

function focusClientBadgeMarker(mapState, badgeMarker, fallbackMarker, animate = false) {
  if (!mapState?.map || mapState.isDisposed) {
    return false;
  }

  const fallbackLatLng = badgeMarker?.getLatLng?.() || fallbackMarker?.getLatLng?.();
  const badgeLayer = mapState.stopBadgeLayer || null;
  if (badgeMarker && typeof badgeLayer?.zoomToShowLayer === "function") {
    badgeLayer.zoomToShowLayer(badgeMarker, () => {
      emphasizeBadgeMarker(mapState, badgeMarker);
      if (!fallbackLatLng || mapState.map.getZoom() >= ROUTE_SELECTION_FOCUS_ZOOM_LEVEL) {
        return;
      }

      mapState.map.setView(fallbackLatLng, ROUTE_SELECTION_FOCUS_ZOOM_LEVEL, { animate });
    });
    return true;
  }

  if (!fallbackLatLng) {
    return false;
  }

  const nextZoom = Math.max(mapState.map.getZoom(), ROUTE_SELECTION_FOCUS_ZOOM_LEVEL);
  mapState.map.setView(fallbackLatLng, nextZoom, { animate });
  emphasizeBadgeMarker(mapState, badgeMarker);
  return true;
}

function getMarkerSelectionPalette(stopType) {
  if (stopType === "loading") {
    return {
      color: LOADING_ROUTE_SELECTED_STROKE,
      fillColor: LOADING_ROUTE_SELECTED_FILL,
    };
  }

  return {
    color: DELIVERY_ROUTE_SELECTED_STROKE,
    fillColor: DELIVERY_ROUTE_SELECTED_FILL,
  };
}

function restoreMapBaseView(mapState) {
  if (!mapState?.map || mapState.isDisposed || !mapState?.baseView) {
    return;
  }

  if (mapState.baseView.mode === "bounds" && Array.isArray(mapState.baseView.bounds) && mapState.baseView.bounds.length) {
    window.L?.latLngBounds && mapState.map.fitBounds(window.L.latLngBounds(mapState.baseView.bounds), {
      padding: Array.isArray(mapState.baseView.padding) ? mapState.baseView.padding : [28, 28],
      animate: true,
    });
    return;
  }

  if (Array.isArray(mapState.baseView.center) && mapState.baseView.center.length === 2) {
    mapState.map.setView(mapState.baseView.center, mapState.baseView.zoom, { animate: true });
  }
}

function syncMarkerSelection(mapState, selectedClientId, selectedStopType) {
  if (!mapState?.map || mapState.isDisposed || !Array.isArray(mapState.markers)) {
    return null;
  }

  let selectedMarker = null;
  let fallbackSelectedMarker = null;

  mapState.markers.forEach((marker) => {
    if (!hasMountedMarker(mapState, marker)) {
      return;
    }

    const defaultStyle = marker.__defaultStyle || {};
    const matchesClient = Boolean(selectedClientId) && marker.__clientId === selectedClientId;
    const matchesStopType = !selectedStopType || !marker.__stopType || marker.__stopType === selectedStopType;
    const isSelected = matchesClient && matchesStopType;
    const selectionPalette = isSelected
      ? getMarkerSelectionPalette(selectedStopType || marker.__stopType)
      : null;

    marker.setStyle?.({
      weight: isSelected ? 4 : (defaultStyle.weight || 3),
      color: isSelected ? selectionPalette.color : (defaultStyle.color || "#ffffff"),
      fillColor: isSelected ? selectionPalette.fillColor : (defaultStyle.fillColor || DEPOT_ROUTE_COLOR),
      fillOpacity: isSelected ? 1 : (defaultStyle.fillOpacity || 0.98),
      opacity: defaultStyle.opacity || 1,
    });

    marker.setRadius?.(
      isSelected
        ? Math.max((defaultStyle.radius || 8) + 2, 10)
        : (defaultStyle.radius || 8),
    );

    if (matchesClient && !fallbackSelectedMarker) {
      fallbackSelectedMarker = marker;
    }

    if (isSelected) {
      selectedMarker = marker;
    }
  });

  return selectedMarker || fallbackSelectedMarker;
}

export default function useRouteSelectionSync({
  mapRenderMeta = null,
  mapStateRef,
  selectedClientId = null,
  selectedStopType = "",
  selectionRequest = null,
}) {
  const previousSelectedClientIdRef = useRef(selectedClientId);
  const mapRenderSignature = mapRenderMeta
    ? `${mapRenderMeta.category || "unknown"}:${mapRenderMeta.markerCount || 0}:${mapRenderMeta.rendered ? "1" : "0"}:${mapRenderMeta.routePointCount || 0}`
    : "null";
  const requestToken = selectionRequest && Number.isInteger(selectionRequest.token)
    ? selectionRequest.token
    : 0;

  useEffect(() => {
    const mapState = mapStateRef?.current || null;
    const selectedMarker = syncMarkerSelection(mapState, selectedClientId, selectedStopType);
    const selectedBadgeMarker = resolveClientBadgeMarker(mapState, selectedClientId, selectedStopType);

    if (
      selectedClientId
      && mapState?.map
      && mapState.isDisposed !== true
      && selectionRequest?.centerMap
      && selectionRequest?.clientId === selectedClientId
    ) {
      focusClientBadgeMarker(
        mapState,
        selectedBadgeMarker,
        hasMountedMarker(mapState, selectedMarker) ? selectedMarker : null,
        false,
      );
    }

    if (!selectedClientId && previousSelectedClientIdRef.current) {
      clearFocusedBadgeMarkers(mapState);
      restoreMapBaseView(mapState);
    }

    previousSelectedClientIdRef.current = selectedClientId;
  }, [mapRenderSignature, mapStateRef, requestToken, selectedClientId, selectedStopType, selectionRequest]);
}